import type { ThemePreferenceId } from '@/lib/api';

export type SystemThemePreferenceId = Extract<
  ThemePreferenceId,
  '7cors-light' | '7cors-dark'
>;

export const THEME_OPTIONS = [
  {
    id: '7cors-light',
    label: 'Claro',
    description: 'Modo claro estilo GitHub: fundo branco, marca azul.',
  },
  {
    id: '7cors-dark',
    label: 'Escuro',
    description: 'Modo escuro suave estilo GitHub: cinza-azulado, marca azul.',
  },
  {
    id: '7cors-system',
    label: 'Sistema',
    description: 'Alterna automaticamente entre claro e escuro do dispositivo.',
  },
] as const satisfies ReadonlyArray<{
  id: ThemePreferenceId;
  label: string;
  description: string;
}>;

export function normalizeTheme(value: string | null | undefined): ThemePreferenceId {
  // Tema "Padrao" (7cors-client) foi aposentado: qualquer valor legado migra
  // para o claro (que agora carrega a paleta v2.0). Default tambem = claro.
  if (value === 'client' || value === '7cors-client') return '7cors-light';
  if (value === 'light') return '7cors-light';
  if (value === 'dark') return '7cors-dark';
  if (value === 'system') return '7cors-system';
  return THEME_OPTIONS.some((theme) => theme.id === value)
    ? (value as ThemePreferenceId)
    : '7cors-light';
}

export function isSystemThemePreferenceId(value: unknown): value is SystemThemePreferenceId {
  return value === '7cors-light' || value === '7cors-dark';
}

function writeThemeToDom(theme: ThemePreferenceId) {
  document.documentElement.dataset.themePreference = theme;
  document.documentElement.dataset.theme = theme;
}

export function getSystemThemePreference(): SystemThemePreferenceId {
  if (typeof window === 'undefined' || !('matchMedia' in window)) {
    return '7cors-light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? '7cors-dark'
    : '7cors-light';
}

export function resolveThemeForDom(
  theme: ThemePreferenceId,
  systemThemePreference: SystemThemePreferenceId = getSystemThemePreference(),
): ThemePreferenceId {
  return theme === '7cors-system' ? systemThemePreference : theme;
}

export function applyTheme(
  theme: ThemePreferenceId,
  options: { systemThemePreference?: SystemThemePreferenceId | null } = {},
) {
  const domTheme = resolveThemeForDom(
    theme,
    options.systemThemePreference ?? getSystemThemePreference(),
  );
  writeThemeToDom(theme);
  document.documentElement.dataset.theme = domTheme;
}

export const THEME_SYNC_CHANNEL = '7barber-theme-sync';
export const THEME_SYNC_EVENT = '7barber-theme-sync';

export function notifyThemeSynced(
  theme: ThemePreferenceId,
  systemThemePreference?: SystemThemePreferenceId | null,
) {
  const payload = {
    type: 'themePreferenceUpdated',
    themePreference: theme,
    systemThemePreference: systemThemePreference ?? null,
    persistedAt: Date.now(),
  } as const;

  window.dispatchEvent(new CustomEvent(THEME_SYNC_EVENT, { detail: payload }));

  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel(THEME_SYNC_CHANNEL);
    channel.postMessage(payload);
    channel.close();
  }
}
